import type { Declaration, DeclarationType } from '../types/declarations';
import { DECLARATION_TYPES } from '../types/declarations';

// Données mock complètes pour les déclarations RDC
export const generateMockDeclaration = (type: DeclarationType, overrides?: Partial<Declaration>): Declaration => {
  const declarationType = DECLARATION_TYPES[type];
  
  const baseDeclaration: Declaration = {
    id: Math.random().toString(36).substr(2, 9),
    reference: `DCL-${type}-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    type,
    period: new Date().toISOString().split('T')[0],
    periodicity: declarationType.periodicity,
    amount: Math.floor(Math.random() * 10000000) + 100000, // Entre 100,000 et 10,100,000 CDF
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 jours
    status: 'draft',
    ...overrides
  };

  // Ajouter des frais et pénalités selon le type et le statut
  if (Math.random() > 0.7) {
    baseDeclaration.additionalFees = Math.floor(baseDeclaration.amount * 0.05); // 5% de frais
  }

  if (Math.random() > 0.8) {
    baseDeclaration.penalties = Math.floor(baseDeclaration.amount * 0.02); // 2% de pénalité
  }

  // Ajouter des documents selon le statut
  if (baseDeclaration.status !== 'draft') {
    baseDeclaration.declarationForm = `form_${baseDeclaration.reference}.pdf`;
    
    if (baseDeclaration.status === 'validated' || Math.random() > 0.5) {
      baseDeclaration.justificationDocument = `justif_${baseDeclaration.reference}.pdf`;
    }

    if (Math.random() > 0.6) {
      baseDeclaration.attachments = [
        {
          id: Math.random().toString(36).substr(2, 9),
          name: `piece_complementaire_${baseDeclaration.reference}.pdf`,
          type: 'supporting_document',
          url: `/api/declarations/${baseDeclaration.id}/attachments/1`,
          size: Math.floor(Math.random() * 2048000) + 512000, // Entre 500KB et 2MB
          uploadedAt: new Date().toISOString()
        }
      ];
    }
  }

  // Ajouter des notes selon le type de déclaration
  if (Math.random() > 0.7) {
    const notes = [
      'Déclaration soumise dans les délais réglementaires.',
      'Vérification des pièces justificatives en cours.',
      'Montant calculé selon le barème en vigueur.',
      'Déclaration modifiée suite aux observations de l\'administration.',
      'Paiement effectué par virement bancaire.',
      'Documents complémentaires fournis sur demande.'
    ];
    baseDeclaration.notes = notes[Math.floor(Math.random() * notes.length)];
  }

  // Ajouter des informations de soumission selon le statut
  if (baseDeclaration.status !== 'draft') {
    baseDeclaration.submittedAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString();
    baseDeclaration.submittedBy = 'Comptable Principal';
  }

  if (baseDeclaration.status === 'validated') {
    baseDeclaration.validatedAt = new Date(Date.now() - Math.random() * 15 * 24 * 60 * 60 * 1000).toISOString();
    baseDeclaration.validatedBy = 'Agent DGI';
  }

  return baseDeclaration;
};

// Données mock pour différents types de déclarations
export const mockDeclarations: Declaration[] = [
  // IBP - Impôt sur les Bénéfices et Profits
  generateMockDeclaration('IBP', {
    status: 'validated',
    amount: 2500000,
    period: '2024-12-31'
  }),

  // IPR - Impôt Professionnel sur les Rémunérations
  generateMockDeclaration('IPR', {
    status: 'submitted',
    amount: 180000,
    period: '2024-11-01',
    additionalFees: 9000
  }),

  // TVA - Taxe sur la Valeur Ajoutée
  generateMockDeclaration('TVA', {
    status: 'pending',
    amount: 850000,
    period: '2024-11-01'
  }),

  // CNSS - Caisse Nationale de Sécurité Sociale
  generateMockDeclaration('CNSS', {
    status: 'rejected',
    amount: 420000,
    period: '2024-10-01',
    penalties: 8400
  }),

  // TPI - Taxe sur les Propriétés Immobilières
  generateMockDeclaration('TPI', {
    status: 'rejected',
    amount: 75000,
    period: '2024-01-01',
    notes: 'Document justificatif de propriété manquant.'
  }),

  // TE - Taxe d\'Exportation
  generateMockDeclaration('TE', {
    status: 'validated',
    amount: 320000,
    period: '2024-11-01'
  }),

  // TCR - Taxe de Circulation Routière
  generateMockDeclaration('TCR', {
    status: 'draft',
    amount: 45000,
    period: '2024-12-01'
  }),

  // INPP - Institut National de Préparation Professionnelle
  generateMockDeclaration('INPP', {
    status: 'submitted',
    amount: 95000,
    period: '2024-11-01'
  }),

  // TSD - Taxe de Solidarité pour le Développement
  generateMockDeclaration('TSD', {
    status: 'validated',
    amount: 650000,
    period: '2024-10-01'
  }),

  // TPU - Taxe de Publicité
  generateMockDeclaration('TPU', {
    status: 'pending',
    amount: 125000,
    period: '2024-11-01'
  })
];

// Fonction pour générer des statistiques mock
export const generateMockDeclarationStats = () => ({
  totalDeclarations: mockDeclarations.length,
  byStatus: {
    draft: mockDeclarations.filter(d => d.status === 'draft').length,
    pending: mockDeclarations.filter(d => d.status === 'pending').length,
    submitted: mockDeclarations.filter(d => d.status === 'submitted').length,
    validated: mockDeclarations.filter(d => d.status === 'validated').length,
    rejected: mockDeclarations.filter(d => d.status === 'rejected').length
  },
  totalAmount: mockDeclarations.reduce((sum, d) => sum + d.amount, 0),
  totalPenalties: mockDeclarations.reduce((sum, d) => sum + (d.penalties || 0), 0),
  totalFees: mockDeclarations.reduce((sum, d) => sum + (d.additionalFees || 0), 0),
  byCategory: {
    direct_tax: mockDeclarations.filter(d => ['IBP', 'IPR', 'IRCM', 'IRVM', 'IPF'].includes(d.type)).length,
    indirect_tax: mockDeclarations.filter(d => ['TVA', 'TPI', 'TE', 'TCR', 'TAD', 'TRD'].includes(d.type)).length,
    social_contribution: mockDeclarations.filter(d => ['CNSS', 'INPP', 'ONEM'].includes(d.type)).length,
    special_tax: mockDeclarations.filter(d => ['TSD', 'TPU', 'TSE', 'AUTRES'].includes(d.type)).length
  }
});

export default mockDeclarations;
